import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import {
  loadClause,
  findClauseAcrossNorms,
  getChildClauses,
  getActiveStandardIds,
  legacyNormKeyToId,
} from "../utils/loadClauseFromV1";
import { getStandardById } from "../modules/standards/registry";

// Aceita:
//   <ClauseModal clauseId="4.1" onClose={...} />                → todas normas ativas
//   <ClauseModal clauseId="4.1" normaId="iso9001" onClose={...} /> → só essa norma
//   <ClauseModal clauseId="4.1" normaId="q" onClose={...} />    → legacy q/a/s (auto-converte)
export function ClauseModal({ clauseId: rawId, normaId, onClose, onNavigate }) {
  if (rawId == null) return null;

  const resolvedNormaId = normaId && normaId.length === 1 ? legacyNormKeyToId(normaId) : normaId;

  // Coletar entries (norma + cláusula) a renderizar
  let entries = [];
  if (resolvedNormaId) {
    const single = loadClause(resolvedNormaId, rawId);
    if (single) entries = [{ normaId: resolvedNormaId, ...single }];
  } else {
    const allActive = new Set(getActiveStandardIds());
    entries = findClauseAcrossNorms(rawId).filter(e => allActive.has(e.normaId));
    if (entries.length === 0) entries = findClauseAcrossNorms(rawId);
  }

  const clauseKey = String(rawId)
    .replace(/\s*\(.*\)\s*/g, "")
    .replace(/[—–-]\s.*/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")[0];

  if (entries.length === 0) {
    return (
      <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground mb-1">cláusula</div>
            <DialogTitle className="font-display text-2xl font-semibold tracking-tight">{clauseKey}</DialogTitle>
          </DialogHeader>
          <div className="text-muted-foreground text-sm py-4">
            Texto normativo não disponível para esta cláusula nas normas ativas.
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground mb-1">cláusula</div>
          <DialogTitle className="font-display text-2xl font-semibold tracking-tight">{clauseKey}</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[70vh] pr-2">
          {entries.map(entry => (
            <NormSection
              key={entry.normaId}
              entry={entry}
              clauseKey={clauseKey}
              showAllNorms={!resolvedNormaId}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NormSection({ entry, clauseKey, showAllNorms, onNavigate }) {
  const { normaId, meta, clause } = entry;
  const children = getChildClauses(normaId, clauseKey);
  const registryEntry = getStandardById(normaId);
  const label = meta.canonical || registryEntry?.name || normaId;

  return (
    <div className="mb-6 pb-4 border-b border-border/40 last:border-0">
      {showAllNorms && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Badge variant="default" className="text-[10px]">{label}</Badge>
          {meta.kind && <KindBadge kind={meta.kind} />}
          {meta.is_certifiable && <Badge variant="outline" className="text-[9px] uppercase tracking-wider">Certificável</Badge>}
        </div>
      )}
      <ClauseBody clause={clause} onNavigate={onNavigate} />
      {children.map(c => (
        <div key={c.number} className="mt-4 pt-3 border-t border-border/30">
          <div className="font-mono text-[11px] font-semibold text-primary mb-1.5 tracking-wide">{c.number}</div>
          <ClauseBody clause={c} onNavigate={onNavigate} />
        </div>
      ))}
    </div>
  );
}

function ClauseBody({ clause, onNavigate }) {
  return (
    <>
      {clause.title && (
        <div className="font-display text-[14px] font-semibold text-foreground tracking-tight mb-2">
          {clause.title}
        </div>
      )}
      <ClauseMetaRow clause={clause} />
      {clause.preamble && (
        <p className="text-foreground text-[13px] leading-relaxed mb-3">{clause.preamble}</p>
      )}
      {clause.items && Object.keys(clause.items).length > 0 && (
        <div className="mb-3">
          {Object.values(clause.items).map((item, i) => (
            <Item key={i} item={item} />
          ))}
        </div>
      )}
      {clause.postamble && (
        <p className="text-foreground text-[13px] leading-relaxed mt-2 mb-3">{clause.postamble}</p>
      )}
      {clause.annotations && clause.annotations.length > 0 && (
        <div className="mt-2 mb-2">
          {clause.annotations.map((a, i) => (
            <div key={i} className="text-muted-foreground text-[11px] italic leading-relaxed mb-1.5 pl-2 border-l-2 border-muted-foreground/30">
              {annotationLabel(a)} {a.text}
            </div>
          ))}
        </div>
      )}
      {clause.cross_refs && clause.cross_refs.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border/40">
          <div className="text-[9px] font-mono uppercase tracking-wider text-primary/70 mb-1.5">Referências cruzadas</div>
          <div className="flex flex-wrap gap-1">
            {clause.cross_refs.map((ref, i) => (
              <button
                key={i}
                onClick={() => onNavigate && onNavigate(ref)}
                className="bg-primary/10 border border-primary/25 rounded-md px-2 py-0.5 text-[10px] text-primary cursor-pointer font-mono hover:bg-primary/20 transition-colors"
              >
                {ref}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function Item({ item }) {
  return (
    <div className="mb-1.5">
      <div className="pl-4 text-foreground text-[13px] leading-relaxed">
        <span className="font-mono text-primary mr-1">{item.ref})</span>
        {item.text}
      </div>
      {item.subitems && Object.keys(item.subitems).length > 0 && (
        <div className="pl-8 mt-1">
          {Object.values(item.subitems).map((sub, i) => (
            <div key={i} className="text-foreground text-[12px] leading-relaxed mb-1">
              <span className="font-mono text-primary/70 mr-1">{sub.ref})</span>
              {sub.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClauseMetaRow({ clause }) {
  const chips = [];
  if (clause.requirement_type) chips.push({ k: "tipo", v: clause.requirement_type });
  if (clause.pdca_phase) chips.push({ k: "pdca", v: clause.pdca_phase });
  if (clause.hls_section != null) chips.push({ k: "hls", v: String(clause.hls_section) });
  if (chips.length === 0) return null;
  return (
    <div className="flex gap-1.5 flex-wrap mb-2">
      {chips.map(c => (
        <span key={c.k} className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary/60 text-muted-foreground">
          {c.k}: {c.v}
        </span>
      ))}
    </div>
  );
}

function KindBadge({ kind }) {
  const styles = {
    requirements: "bg-primary/15 text-primary border-primary/30",
    methodology: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    vocabulary: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    guidance: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    regulation: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  };
  return (
    <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${styles[kind] || "bg-secondary text-muted-foreground border-border"}`}>
      {kind}
    </span>
  );
}

function annotationLabel(a) {
  const t = a.type || "note";
  const labels = { note: "NOTA", example: "EXEMPLO", remark: "OBS" };
  const num = a.number ? ` ${a.number}` : "";
  return `${labels[t] || t.toUpperCase()}${num}:`;
}
